/**
 * BookingSuccess — post-booking screen of the one-page public flow. Shows the
 * booking reference plus two QUIET follow-ups (mockup §0b: neither blocks the
 * booking): "Apply for payment terms" mounts the existing SignUp credit
 * application (email already prefilled via booking_customer_form), and
 * "Prefer to book through an agent?" mounts the existing BookViaAgent form.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from 'antd';
import SignUp from '../SignUp';
import BookViaAgent from '../BookViaAgent';
import { color, radius, shadow, transition } from '../../../styles/designTokens';
import { SectionCard, QuietButton } from './confirmTheme';

const Center = styled.div`
  text-align: center;
  padding: 26px 0 8px;
`;

const SuccessTitle = styled.h2`
  margin: 14px 0 4px;
  font-size: 20px;
  font-weight: 600;
  color: ${color.navy};
`;

const SuccessBody = styled.p`
  margin: 0 auto;
  max-width: 46ch;
  font-size: 14px;
  line-height: 1.6;
  color: ${color.ink2};
`;

const RefBadge = styled.div`
  display: inline-block;
  margin-top: 14px;
  padding: 8px 16px;
  background: ${color.okSoft};
  border: 1px solid ${color.okLine};
  border-radius: ${radius.base};
  font-size: 14px;
  font-weight: 600;
  color: ${color.ok};
  font-variant-numeric: tabular-nums;
`;

const FollowUps = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 22px;
  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const FollowUpCard = styled.button`
  text-align: left;
  background: #fff;
  border: 1px solid ${color.line};
  border-radius: ${radius.card};
  box-shadow: ${shadow.card};
  padding: 16px 18px;
  cursor: pointer;
  transition: ${transition};
  &:hover,
  &:focus {
    outline: none;
    border-color: ${color.primary};
    box-shadow: 0 0 0 3px rgba(30, 148, 241, 0.18);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const FollowUpTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${color.navy};
`;

const FollowUpHint = styled.div`
  margin-top: 4px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${color.ink2};
`;

/** @readonly @enum {string} */
const SuccessView = Object.freeze({
  SUMMARY: 'summary',
  CREDIT_APP: 'creditApp',
  AGENT: 'agent',
});

class BookingSuccess extends Component {
  state = { view: SuccessView.SUMMARY };

  show = (view) => () => {
    this.setState({ view });
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0 });
    }
  };

  render() {
    const { bookingRef } = this.props;
    const { view } = this.state;

    if (view !== SuccessView.SUMMARY) {
      return (
        <div>
          <div style={{ padding: '14px 0 0' }}>
            <QuietButton onClick={this.show(SuccessView.SUMMARY)}>
              <Icon type="arrow-left" /> Back to your booking
            </QuietButton>
          </div>
          <SectionCard>
            {view === SuccessView.CREDIT_APP ? <SignUp /> : <BookViaAgent />}
          </SectionCard>
        </div>
      );
    }

    return (
      <SectionCard aria-label="Booking received">
        <Center>
          <Icon
            type="check-circle"
            theme="twoTone"
            twoToneColor={color.ok}
            style={{ fontSize: '56px' }}
          />
          <SuccessTitle>Thanks for shipping with us!</SuccessTitle>
          <SuccessBody>
            Your booking request has been received. We will get in touch with
            you shortly to confirm the details.
          </SuccessBody>
          {bookingRef && <RefBadge>Booking reference: {bookingRef}</RefBadge>}
        </Center>

        <FollowUps>
          <FollowUpCard type="button" onClick={this.show(SuccessView.CREDIT_APP)}>
            <FollowUpTitle>Apply for payment terms</FollowUpTitle>
            <FollowUpHint>
              Optional — approval takes 1–2 business days and never delays this
              booking.
            </FollowUpHint>
          </FollowUpCard>
          <FollowUpCard type="button" onClick={this.show(SuccessView.AGENT)}>
            <FollowUpTitle>Prefer to book through an agent?</FollowUpTitle>
            <FollowUpHint>
              Have one of our partner agents manage this shipment with you.
            </FollowUpHint>
          </FollowUpCard>
        </FollowUps>
      </SectionCard>
    );
  }
}

BookingSuccess.propTypes = {
  /** primeFreightRef (or booking id) returned by the booking action */
  bookingRef: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BookingSuccess;
